import React, { useCallback, useEffect, useState, useRef } from 'react';
import { Helmet } from 'react-helmet';
import * as Sentry from '@sentry/react';
import { t } from 'ttag';

import useCategoryData from 'hooks/useCategoryData';
import useLocation from 'hooks/useLocation';
import useSearchData from 'hooks/useSearchData';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Heading,
  Skeleton
} from 'components';

import Description from './Description';
import Filters from './Filters/Filters';
import Pagination from './Pagination';
import ProductGrid from './ProductGrid';
import Sorting from './Sorting';
import SubCategories from './SubCategories';

import './Category.css';

/*
 * The Category Page (and Search Results Page) Application
 *
 * The Category Page is a dynamic page that renders the Products of a Category and the Search Results.
 * We use the same component for both pages, because the logic and design is mostly the same, thus, we can reuse the code to reduce the amount of code we have to maintain.
 */

export default function App() {
  const [isInitializing, setIsInitializing] = useState(
    /** @type {boolean} isInitializing - Whether or not the component is ready to render */ true
  );

  const [metaInformation, setMetaInformation] = useState({});

  const [productData, setProductData] = useState(
    /** @type {Array} productData - The Product Data */ []
  );

  const { pathname, search, push } = useLocation();

  const { pageType } = document.querySelector('#category').dataset;

  let data = {};
  let isLoading = true;

  if (pageType === 'category') {
    ({ data, isLoading } = useCategoryData({
      slug: pathname.slice(1),
      // Pass the URL Parameters to the useCategoryData hook
      ...Object.fromEntries(new URLSearchParams(search))
    }));
  }

  if (pageType === 'search') {
    const url = new URL(window.location.href);
    const searchQuery = url.searchParams.get('q');

    if (searchQuery) {
      ({ data, isLoading } = useSearchData({
        sort: 'default',
        // Pass the URL Parameters to the useSearchData hook
        ...Object.fromEntries(new URLSearchParams(search))
      }));
    } else {
      ({ data, isLoading } = useSearchData({
        q: '*',
        sort: 'default',
        // Pass the URL Parameters to the useSearchData hook
        ...Object.fromEntries(new URLSearchParams(search))
      }));
    }
  }

  useEffect(() => {
    if (data) {
      if (data?.meta) {
        setMetaInformation(data.meta);
      }
      if (data?.products) {
        setProductData(data.products);
      }
    }
    if (!isLoading) {
      setIsInitializing(false);
    }
  }, [isLoading, data]);

  const getContent = useCallback(() => {
    if (metaInformation?.content) {
      return { __html: metaInformation.content };
    }
    return { __html: '' };
  }, [metaInformation]);

  /**
   * breadcrumbRef Reference to be used to target the Breadcrumb in the DOM.
   * @type {React.MutableRefObject<null>}
   */
  const breadcrumbRef = useRef(null);

  /**
   * Scroll the Breadcrumb to the end when the metaInformation loads and/or changes.
   * @type {React.useEffect<void>}
   */
  useEffect(() => {
    if (breadcrumbRef.current)
      breadcrumbRef.current.scrollLeft = breadcrumbRef.current.scrollWidth;
  }, [metaInformation]);

  /**
   * Google Tag Manager Pusher
   * Send a 'trackPageView' if the Pathname changes.
   * @type {React.useEffect<void>}
   */
  useEffect(() => {
    window?.ua?.('trackPageView', null, [
      {
        schema: 'iglu:it.uniid/page/jsonschema/1-0-0',
        data: {
          pageType
        }
      }
    ]);
  }, [pathname]);

  return (
    <>
      {/** Render the Meta Information */}
      <Helmet>
        <title>{metaInformation?.meta_title}</title>

        {(parseInt(metaInformation?.pages?.current, 10) || 0) !== 1 && (
          <link
            rel="prev"
            href={`${window.location.origin}${pathname}?page=${
              (parseInt(metaInformation?.pages?.current, 10) || 0) - 1
            }`}
          />
        )}
        {(parseInt(metaInformation?.pages?.current, 10) || 0) <
          (parseInt(metaInformation?.pages?.total, 10) || 0) && (
          <link
            rel="next"
            href={`${window.location.origin}${pathname}?page=${
              (parseInt(metaInformation?.pages?.current, 10) || 0) + 1
            }`}
          />
        )}
      </Helmet>
      {pageType === 'category' && (
        <Helmet>
          <meta itemProp="name" content={metaInformation?.meta_title} />
          <meta
            itemProp="description"
            content={metaInformation?.meta_description}
          />
          <meta itemProp="image" content={metaInformation?.image} />

          <meta name="twitter:title" content={metaInformation?.meta_title} />
          <meta
            name="twitter:description"
            content={metaInformation?.meta_description}
          />
          <meta name="twitter:image" content={metaInformation?.image} />

          <meta property="og:title" content={metaInformation?.meta_title} />
          <meta
            property="og:description"
            content={metaInformation?.meta_description}
          />
          <meta property="og:image" content={metaInformation?.image} />
        </Helmet>
      )}
      {/** Render the Breadcrumbs (Category Page Only) */}
      <Sentry.ErrorBoundary fallback={null}>
        {pageType === 'category' && (
          <div className="bg-white-smoke">
            <div
              ref={breadcrumbRef}
              className="hide-scrollbar container flex min-h-[38px] items-center overflow-x-scroll scroll-smooth px-2 pt-0.5 sm:px-4"
            >
              <Breadcrumb>
                {metaInformation?.breadcrumb &&
                  metaInformation.breadcrumb.map(
                    ({ title, slug: breadcrumbSlug }, index) => (
                      <BreadcrumbItem key={breadcrumbSlug}>
                        {index === metaInformation.breadcrumb.length - 1 ? (
                          <BreadcrumbLink isCurrentPage>{title}</BreadcrumbLink>
                        ) : (
                          <BreadcrumbLink href={breadcrumbSlug}>
                            {title}
                          </BreadcrumbLink>
                        )}
                      </BreadcrumbItem>
                    )
                  )}
              </Breadcrumb>
            </div>
          </div>
        )}
      </Sentry.ErrorBoundary>
      <div className="container flex flex-col gap-4 p-1 sm:p-4">
        {/** Render the Category Title */}
        <Skeleton isLoading={isInitializing} width={256} height={32}>
          <div className="flex min-h-[32px] items-center">
            <Heading as="h1" noMargin>
              {metaInformation?.title}
            </Heading>
          </div>
        </Skeleton>
        {/** Render the Category Description (Category Page Only) */}
        <Sentry.ErrorBoundary fallback={null}>
          {pageType === 'category' && (
            <Description
              description={metaInformation?.description}
              pathname={pathname}
            />
          )}
        </Sentry.ErrorBoundary>
        {/** Render the Category Sub Categories (Category Page Only) */}
        <Sentry.ErrorBoundary fallback={null}>
          {pageType === 'category' && (
            <SubCategories
              subCategories={metaInformation?.sub_categories}
              hasSubCategories={metaInformation?.has_sub_categories}
              pathname={pathname}
              pushUrl={slug => push(slug)}
            />
          )}
        </Sentry.ErrorBoundary>
        {/** Render the Category Filters and Sorting */}
        <Sentry.ErrorBoundary fallback={null}>
          <div className="mt-4 flex items-center gap-2">
            <Filters
              metaInformation={metaInformation}
              isLoading={isLoading}
              pathname={pathname}
              search={search}
              push={push}
            />
            <Sorting
              pathname={pathname}
              search={search}
              push={push}
              isLoading={isInitializing}
            />
          </div>
        </Sentry.ErrorBoundary>
        {/** Render the Category Products */}
        <Skeleton isLoading={isInitializing} width={144} height={20}>
          <p className="text-sm text-dark-gray">
            <span>
              {(parseInt(metaInformation?.pages?.current || 0, 10) - 1) * 60 +
                (productData?.length === 0 ? 0 : 1)}
              {' - '}
              {(parseInt(metaInformation?.pages?.current || 0, 10) - 1) * 60 +
                (productData?.length || 0)}
            </span>{' '}
            {t`of`} {metaInformation?.total} {t`Products`}
          </p>
        </Skeleton>
        <Sentry.ErrorBoundary fallback={null}>
          <ProductGrid
            products={productData}
            search={search}
            categoryTitle={metaInformation?.title || ''}
            isLoading={isLoading}
          />
        </Sentry.ErrorBoundary>
        {/** Render the Category Pagination */}
        <Sentry.ErrorBoundary fallback={null}>
          <div className="flex min-h-[72px] justify-center">
            <Pagination
              pathname={pathname}
              push={push}
              search={search}
              currentPage={metaInformation?.pages?.current || '1'}
              totalPages={metaInformation?.pages?.total || '0'}
            />
          </div>
        </Sentry.ErrorBoundary>
        {/** Render the Category Content (Category Page Only) */}
        {pageType === 'category' && window?.siteId !== '23' && window?.siteId !== '26' && (
          <section className="flex flex-col gap-4">
            {metaInformation?.description && (
              <p className="mb-4 text-sm leading-loose text-black md:hidden">
                {metaInformation.description}
              </p>
            )}
            {/* eslint-disable-next-line react/no-danger */}
            <div className="content" dangerouslySetInnerHTML={getContent()} />
          </section>
        )}
      </div>
    </>
  );
}
